import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Check, X, Pencil, Tags } from "lucide-react";

// Dinamik Ürün Kategorileri — ekle / yeniden adlandır / aktif-pasif.
// İsimler hard-code DEĞİL: tenant'ın product_categories koleksiyonundan gelir.
// Silme bu fazda YOK (pasife alma var). Mutasyonlar products.edit gerektirir.
export default function ProductCategoriesDialog({ open, onOpenChange, canEdit = false }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-categories");
      setCats(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setEditId(null); setNewName(""); load(); } }, [open]);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/product-categories", { name: newName.trim() });
      toast.success("Kategori eklendi");
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveRename = async (id) => {
    if (!editName.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/product-categories/${id}`, { name: editName.trim() });
      toast.success("Kategori güncellendi");
      setEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    setBusy(true);
    try {
      await api.patch(`/product-categories/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Kategori pasife alındı" : "Kategori aktifleştirildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg max-h-[85vh] flex flex-col p-0" data-testid="categories-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Tags className="w-5 h-5 text-cyan-400" /> Ürün Kategorileri
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 px-6 pb-6 space-y-3">
          {canEdit && (
            <div className="flex gap-2">
              <Input
                data-testid="category-add-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                placeholder="Yeni kategori adı"
                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs"
              />
              <Button onClick={add} disabled={busy || !newName.trim()} data-testid="category-add-btn"
                className="bg-cyan-700 hover:bg-cyan-600 rounded-sm text-xs h-8 shrink-0">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Ekle</>}
              </Button>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          ) : (
            <div className="divide-y divide-slate-800/60 border border-slate-800 rounded-sm" data-testid="category-list">
              {cats.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2" data-testid={`category-row-${c.id}`}>
                  {editId === c.id ? (
                    <>
                      <Input
                        data-testid={`category-rename-input-${c.id}`}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(c.id); if (e.key === "Escape") setEditId(null); }}
                        autoFocus
                        className="bg-slate-950 border-slate-700 rounded-sm h-7 text-xs flex-1"
                      />
                      <Button size="sm" variant="ghost" onClick={() => saveRename(c.id)} disabled={busy}
                        data-testid={`category-rename-save-${c.id}`}
                        className="h-7 w-7 p-0 text-emerald-400 hover:bg-emerald-950/40">
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)}
                        data-testid={`category-rename-cancel-${c.id}`}
                        className="h-7 w-7 p-0 text-slate-400 hover:bg-slate-800">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className={`text-xs flex-1 ${c.active ? "text-slate-200" : "text-slate-600 line-through"}`}
                        data-testid={`category-name-${c.id}`}>{c.name}</span>
                      <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                        c.active ? "text-emerald-400 border-emerald-500/30" : "text-slate-500 border-slate-700"}`}
                        data-testid={`category-status-${c.id}`}>
                        {c.active ? "Aktif" : "Pasif"}
                      </span>
                      {canEdit && (
                        <>
                          <Button size="sm" variant="ghost"
                            onClick={() => { setEditId(c.id); setEditName(c.name); }}
                            data-testid={`category-rename-btn-${c.id}`}
                            title="Yeniden adlandır"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggle(c)} disabled={busy}
                            data-testid={`category-toggle-${c.id}`}
                            title={c.active ? "Pasife al" : "Aktifleştir"}
                            className={`h-7 px-2 text-[10px] font-mono uppercase ${
                              c.active ? "text-amber-400 hover:bg-amber-950/40" : "text-emerald-400 hover:bg-emerald-950/40"}`}>
                            {c.active ? "Pasifleştir" : "Aktifleştir"}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
              {!cats.length && (
                <div className="text-center text-xs text-slate-500 py-8">Kategori yok</div>
              )}
            </div>
          )}
          <div className="text-[10px] text-slate-600 font-mono">
            Aktif kategoriler Excel şablonundaki kategori seçim listesine otomatik gelir.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
